import React, { useState, useEffect } from 'react';
import { Download, Upload, Trash2 } from 'lucide-react';
import { Card, Button, Input, Modal, Badge } from '../components/UI';
import { versionAPI } from '../api/apiClient';
import toast from 'react-hot-toast';

/**
 * APK Manager Page
 */
const APKManagerPage = () => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    version: '',
    versionCode: '',
    apkUrl: '',
    changelog: '',
    releaseNotes: '',
    forceUpdate: false,
    fileSize: 0,
  });

  useEffect(() => {
    loadVersions();
  }, []);

  const loadVersions = async () => {
    try {
      setLoading(true);
      const response = await versionAPI.getAll(1, 20);
      setVersions(response.data.data.versions);
    } catch (error) {
      toast.error('Failed to load versions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVersion = async () => {
    try {
      if (!formData.version || !formData.versionCode || !formData.apkUrl) {
        toast.error('Please fill in all required fields');
        return;
      }

      await versionAPI.create({
        ...formData,
        versionCode: parseInt(formData.versionCode),
      });
      toast.success('Version created successfully');
      setIsModalOpen(false);
      resetForm();
      loadVersions();
    } catch (error) {
      toast.error('Failed to create version');
    }
  };

  const resetForm = () => {
    setFormData({
      version: '',
      versionCode: '',
      apkUrl: '',
      changelog: '',
      releaseNotes: '',
      forceUpdate: false,
      fileSize: 0,
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center space-x-3">
            <Download size={32} className="text-purple-400" />
            <span>APK Manager</span>
          </h1>
          <p className="text-gray-400 mt-2">Manage app versions and releases</p>
        </div>
        <Button
          variant="primary"
          onClick={() => setIsModalOpen(true)}
          className="mt-4 md:mt-0 flex items-center space-x-2"
        >
          <Upload size={20} />
          <span>Upload Version</span>
        </Button>
      </div>

      {/* Versions List */}
      <Card>
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : versions.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400">No versions uploaded yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {versions.map((version) => (
              <div
                key={version._id}
                className="bg-gray-700 rounded-lg p-4 flex items-center justify-between hover:bg-gray-600 transition"
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-bold text-white">
                      v{version.version}
                    </h3>
                    {version.isActive && (
                      <Badge variant="success">Active</Badge>
                    )}
                    {version.forceUpdate && (
                      <Badge variant="warning">Force Update</Badge>
                    )}
                  </div>

                  <p className="text-gray-400 text-sm mb-2">
                    {version.releaseNotes}
                  </p>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Code</p>
                      <p className="text-white font-medium">
                        {version.versionCode}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">File Size</p>
                      <p className="text-white font-medium">
                        {formatFileSize(version.fileSize)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Downloads</p>
                      <p className="text-white font-medium">
                        {version.downloadCount}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Released</p>
                      <p className="text-white font-medium">
                        {new Date(version.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <a
                    href={version.apkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-green-400 hover:bg-gray-500 rounded-lg transition"
                  >
                    <Download size={20} />
                  </a>
                  <button className="p-2 text-red-400 hover:bg-gray-500 rounded-lg transition">
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title="Upload New Version"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateVersion}>
              Upload
            </Button>
          </>
        }
      >
        <div className="space-y-4 max-h-96 overflow-y-auto">
          <Input
            label="Version (e.g., 1.0.0)"
            value={formData.version}
            onChange={(e) => setFormData({ ...formData, version: e.target.value })}
            placeholder="1.0.0"
          />

          <Input
            label="Version Code"
            type="number"
            value={formData.versionCode}
            onChange={(e) =>
              setFormData({ ...formData, versionCode: e.target.value })
            }
            placeholder="1"
          />

          <Input
            label="APK Download URL"
            value={formData.apkUrl}
            onChange={(e) => setFormData({ ...formData, apkUrl: e.target.value })}
            placeholder="https://example.com/app-v1.0.0.apk"
          />

          <Input
            label="Changelog"
            value={formData.changelog}
            onChange={(e) =>
              setFormData({ ...formData, changelog: e.target.value })
            }
            placeholder="Bug fixes and improvements"
          />

          <Input
            label="Release Notes"
            value={formData.releaseNotes}
            onChange={(e) =>
              setFormData({ ...formData, releaseNotes: e.target.value })
            }
            placeholder="Initial release"
          />

          <Input
            label="File Size (bytes)"
            type="number"
            value={formData.fileSize}
            onChange={(e) =>
              setFormData({ ...formData, fileSize: parseInt(e.target.value) })
            }
            placeholder="0"
          />

          <div className="flex items-center">
            <input
              type="checkbox"
              checked={formData.forceUpdate}
              onChange={(e) =>
                setFormData({ ...formData, forceUpdate: e.target.checked })
              }
              className="w-4 h-4 text-purple-600 rounded"
            />
            <label className="ml-2 text-gray-300">Force Update</label>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default APKManagerPage;
